import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { TransactionTemplate } from '@/lib/types';
import api, { endpoints } from '@/services/api';
import { getApiErrorMessage } from '@/lib/utils';

interface TemplatesState {
  templates: TransactionTemplate[];
  isLoading: boolean;
  error: string | null;
}

const initialState: TemplatesState = {
  templates: [],
  isLoading: false,
  error: null,
};

export const fetchTemplates = createAsyncThunk(
  'templates/fetchTemplates',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.transactionTemplates.list);
      return (response.data.data || response.data) as TransactionTemplate[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch templates'));
    }
  }
);

export const createTemplate = createAsyncThunk(
  'templates/createTemplate',
  async (data: Partial<TransactionTemplate>, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.transactionTemplates.create, data);
      return (response.data.data || response.data) as TransactionTemplate;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create template'));
    }
  }
);

export const updateTemplate = createAsyncThunk(
  'templates/updateTemplate',
  async ({ id, data }: { id: string; data: Partial<TransactionTemplate> }, { rejectWithValue }) => {
    try {
      const response = await api.put(endpoints.transactionTemplates.update(id), data);
      return (response.data.data || response.data) as TransactionTemplate;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update template'));
    }
  }
);

export const deleteTemplate = createAsyncThunk(
  'templates/deleteTemplate',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(endpoints.transactionTemplates.delete(id));
      return id;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete template'));
    }
  }
);

export const createFromTransaction = createAsyncThunk(
  'templates/createFromTransaction',
  async (txId: string, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.transactionTemplates.fromTransaction(txId));
      return (response.data.data || response.data) as TransactionTemplate;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create template from transaction'));
    }
  }
);

const templatesSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {
    clearTemplateError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    // Fetch
    builder.addCase(fetchTemplates.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(fetchTemplates.fulfilled, (state, action) => { state.isLoading = false; state.templates = action.payload; });
    builder.addCase(fetchTemplates.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // Create
    builder.addCase(createTemplate.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(createTemplate.fulfilled, (state, action) => { state.isLoading = false; state.templates.push(action.payload); });
    builder.addCase(createTemplate.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // Update
    builder.addCase(updateTemplate.fulfilled, (state, action) => {
      const idx = state.templates.findIndex(t => t._id === action.payload._id);
      if (idx !== -1) state.templates[idx] = action.payload;
    });
    builder.addCase(updateTemplate.rejected, (state, action) => { state.error = action.payload as string; });

    // Delete
    builder.addCase(deleteTemplate.fulfilled, (state, action) => {
      state.templates = state.templates.filter(t => t._id !== action.payload);
    });
    builder.addCase(deleteTemplate.rejected, (state, action) => { state.error = action.payload as string; });

    // Create from transaction
    builder.addCase(createFromTransaction.fulfilled, (state, action) => { state.templates.push(action.payload); });
    builder.addCase(createFromTransaction.rejected, (state, action) => { state.error = action.payload as string; });
  },
});

export const { clearTemplateError } = templatesSlice.actions;
export default templatesSlice.reducer;
